import React, { useState, useEffect, useRef } from 'react';
import { FaBox, FaCube, FaWeight, FaFileAlt, FaPlus, FaTrash, FaCheckCircle } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { removeBackground } from '@imgly/background-removal';
import * as submasterService from '../../services/submaster.service';
import { productService } from '../../services/product.service';
import { PRODUCT } from '../../constant';
import TagInput from '../../components/Product/TagInput';
import {
  INITIAL_FORM,
  compressImage,
  emptyPackRow,
  fetchNextProductUid,
  getImageDisplaySrc,
  mapApiProductToForm,
  readFileAsDataUrl,
  revokePreviewIfBlob,
  sameSubmasterId,
  validateProductForm,
} from '../../components/Product/productUtils';
import './Product.css';

const ProductMaster = () => {
  const [submasters, setSubmasters] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [processingImages, setProcessingImages] = useState(false);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const { uid } = useParams();
  const isEditMode = !!uid;
  const generatedUIDRef = useRef(null);
  const productRef = useRef(null);

  const [formData, setFormData] = useState(() => ({ ...INITIAL_FORM }));

  const [errors, setErrors] = useState({});

  // Generate UID on mount or load from editing product
  useEffect(() => {
    let mounted = true;

    const loadProduct = async () => {
      if (isEditMode && uid) {
        try {
          const editingProduct = await productService.getProductByUID(uid);
          if (editingProduct && mounted) {
            setFormData(mapApiProductToForm(editingProduct));

            if (editingProduct.images && Array.isArray(editingProduct.images)) {
              setUploadedImages(
                editingProduct.images.map((img) => ({
                  id: img.id,
                  path: img.path,
                  preview: img.preview || img.path,
                  is_main: img.is_main,
                  sort_order: img.sort_order,
                  // Note: no 'file' property for existing images - this distinguishes them from newly uploaded images
                }))
              );
              const mainImageIdx = editingProduct.images.findIndex((img) => img.is_main);
              setMainImageIndex(mainImageIdx >= 0 ? mainImageIdx : 0);
            }
          } else if (mounted) {
            setMessage({
              type: 'error',
              text: PRODUCT.ALERT_NOT_FOUND_UID(uid),
            });
          }
        } catch (e) {
          console.error('Error loading product:', e);
          if (mounted) {
            setMessage({
              type: 'error',
              text: PRODUCT.ALERT_LOAD_FAILED,
            });
          }
        }
      }
    };

    const initializeForm = async () => {
      if (!isEditMode) {
        // Only generate UID once, store in ref to prevent regeneration on re-renders
        if (!generatedUIDRef.current) {
          const newUid = await fetchNextProductUid();
          generatedUIDRef.current = newUid;
        }
        if (mounted) {
          setFormData((f) => ({ ...f, uid: generatedUIDRef.current }));
        }
      } else {
        loadProduct();
      }
    };

    const loadSubmasters = async () => {
      try {
        const results = {};
        await Promise.all(
          PRODUCT.SUBMASTER_TYPES.map(async (t) => {
            try {
              const items = await submasterService.getItems(t.key);
              results[t.key] = Array.isArray(items) ? items : items.data || [];
            } catch (e) {
              console.error(`Failed to load ${t.key}`, e);
              results[t.key] = [];
            }
          })
        );
        if (!mounted) return;
        console.log('Submasters loaded:', results);
        setSubmasters(results);
      } catch (e) {
        console.error('Failed to load submasters', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    setLoading(true);
    initializeForm();
    loadSubmasters();

    return () => {
      mounted = false;
    };
  }, [isEditMode, uid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleMultiSelect = (fieldName, selectedId) => {
    const normalized = String(selectedId);
    setFormData((f) => {
      const current = f[fieldName] || [];
      const updated = current.some((id) => sameSubmasterId(id, normalized))
        ? current.filter((id) => !sameSubmasterId(id, normalized))
        : [...current, normalized];
      return { ...f, [fieldName]: updated };
    });
    setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
  };

  const removeTag = (fieldName, tagId) => {
    setFormData((f) => ({
      ...f,
      [fieldName]: f[fieldName].filter((id) => !sameSubmasterId(id, tagId)),
    }));
  };

  const addPackRow = (packType) => {
    setFormData((f) => ({
      ...f,
      [packType]: [...f[packType], emptyPackRow()],
    }));
  };

  const removePackRow = (packType, index) => {
    setFormData((f) => ({
      ...f,
      [packType]: f[packType].filter((_, i) => i !== index),
    }));
  };

  const handleDelete = async () => {
    if (!isEditMode || !formData.uid) {
      setMessage({ type: 'danger', text: PRODUCT.ALERT_DELETE_NEW });
      return;
    }

    const confirmDelete = window.confirm(
      PRODUCT.DELETE_CONFIRM(formData.itemNo, formData.uid),
    );

    if (!confirmDelete) return;

    try {
      // Find product by UID to get database ID
      const product = await productService.getProductByUID(formData.uid);
      if (!product) {
        setMessage({ type: 'danger', text: PRODUCT.ALERT_PRODUCT_NOT_FOUND });
        return;
      }

      await productService.deleteProduct(product.id);

      setMessage({
        type: 'success',
        text: PRODUCT.SUCCESS_DELETED(formData.itemNo),
      });

      setTimeout(() => {
        window.location.href = PRODUCT.ROUTE_CATALOG;
      }, 1500);
    } catch (err) {
      console.error('Error deleting product:', err);
      setMessage({
        type: 'danger',
        text: PRODUCT.ALERT_DELETE_FAILED,
      });
    }
  };

  const handlePackChange = (packType, index, field, value) => {
    setFormData((f) => ({
      ...f,
      [packType]: f[packType].map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const handleImageUpload = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []).filter((f) => f.type.startsWith('image/'));
    input.value = '';

    if (files.length === 0) return;

    if (uploadedImages.length + files.length > 5) {
      setMessage({ type: 'warning', text: PRODUCT.ALERT_MAX_IMAGES });
      return;
    }

    setProcessingImages(true);
    setMessage(null);
    const newEntries = [];
    let anyBgRemovalFailed = false;
    let compressionInfo = { total: files.length, compressed: 0 };

    try {
      for (const file of files) {
        let processedFile = file;
        let preview;
        
        try {
          // Step 1: Remove background
          const blob = await removeBackground(file);
          const baseName = (file.name.replace(/\.[^.]+$/, '') || 'product').replace(/[^\w.-]+/g, '_');
          processedFile = new File([blob], `${baseName}-nobg.png`, { type: 'image/png' });
          
          // Step 2: Compress the image
          try {
            const compressedFile = await compressImage(processedFile);
            const originalSize = processedFile.size;
            const compressedSize = compressedFile.size;
            const savings = Math.round(((originalSize - compressedSize) / originalSize) * 100);
            
            console.log(
              `Image compressed: ${(originalSize / 1024).toFixed(2)}KB → ${(compressedSize / 1024).toFixed(2)}KB (${savings}% reduction)`
            );
            
            if (savings > 0) {
              compressionInfo.compressed++;
            }
            processedFile = compressedFile;
          } catch (compressionErr) {
            console.warn('Image compression failed, using uncompressed:', compressionErr);
            // Continue with uncompressed version
          }
          
          preview = URL.createObjectURL(processedFile);
        } catch (err) {
          console.error('Background removal failed:', err);
          anyBgRemovalFailed = true;
          
          // Fallback: compress original image
          try {
            const compressedFile = await compressImage(file);
            processedFile = compressedFile;
            const originalSize = file.size;
            const compressedSize = compressedFile.size;
            const savings = Math.round(((originalSize - compressedSize) / originalSize) * 100);
            console.log(
              `Image compressed (no BG removal): ${(originalSize / 1024).toFixed(2)}KB → ${(compressedSize / 1024).toFixed(2)}KB (${savings}% reduction)`
            );
            if (savings > 0) {
              compressionInfo.compressed++;
            }
          } catch (compressionErr) {
            console.warn('Compression also failed, using original:', compressionErr);
            processedFile = file;
          }
          
          preview = await readFileAsDataUrl(processedFile);
        }
        
        newEntries.push({ file: processedFile, preview });
      }

      let messageText = '';
      if (anyBgRemovalFailed) {
        messageText = `Uploaded ${files.length} image(s). Background removal failed for some; originals were compressed.`;
      } else {
        messageText = `Uploaded ${files.length} image(s). All images compressed by average ${Math.round(
          (compressionInfo.compressed / compressionInfo.total) * 100
        )}%.`;
      }
      
      if (compressionInfo.compressed > 0) {
        setMessage({
          type: 'success',
          text: messageText,
        });
      }

      setUploadedImages((prev) => [...prev, ...newEntries]);
    } catch (err) {
      console.error('Image upload processing error:', err);
      setMessage({ type: 'danger', text: PRODUCT.ALERT_IMAGE_PROCESS_FAILED });
    } finally {
      setProcessingImages(false);
    }
  };

  const removeImage = (index) => {
    const prevLen = uploadedImages.length;
    const newLen = prevLen - 1;

    setUploadedImages((prev) => {
      const removed = prev[index];
      if (removed) revokePreviewIfBlob(removed.preview);
      return prev.filter((_, i) => i !== index);
    });

    setMainImageIndex((cur) => {
      if (newLen <= 0) return 0;
      if (index < cur) return cur - 1;
      if (index === cur) return Math.min(cur, newLen - 1);
      return cur >= newLen ? newLen - 1 : cur;
    });
  };

  const validateForm = () => validateProductForm(formData, uploadedImages);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    
    const v = validateForm();
    if (Object.keys(v).length) {
      setErrors(v);
      setMessage({ type: 'danger', text: PRODUCT.ALERT_FIX_ERRORS });
      return;
    }

    if (processingImages) {
      setMessage({ type: 'warning', text: PRODUCT.ALERT_WAIT_IMAGES });
      return;
    }

    setSaving(true);
    try {
      // Prepare data for API
      const productData = {
        uid: formData.uid,
        itemNo: formData.itemNo,
        category: formData.category,
        productName: formData.productName,
        hsCode: formData.hsCode,
        suitableFor: formData.suitableFor,
        uomCount: formData.uomCount,
        lengthDimension: formData.lengthDimension,
        breadthDimension: formData.breadthDimension,
        heightDimension: formData.heightDimension,
        dimensionUnit: formData.dimensionUnit,
        netWeight: formData.netWeight,
        weightUnit: formData.weightUnit,
        moq: formData.moq,
        cost: formData.cost,
        remark: formData.remark,
        mainImageIndex: mainImageIndex,
        color: formData.color,
        finish: formData.finish,
        materials: formData.materials,
        collection: formData.collection,
        packType: formData.packType,
        innerPacks: formData.innerPacks,
        masterPacks: formData.masterPacks,
        images: uploadedImages.map(img => img.file),
        // In edit mode, send IDs of existing images to keep (those without a 'file' property)
        ...(isEditMode && {
          keepImageIds: uploadedImages
            .filter(img => img.id)  // Filter images that have an id (existing images)
            .map(img => img.id),
        }),
      };

      let result;
      if (isEditMode) {
        // Update existing product
        const product = await productService.getProductByUID(formData.uid);
        if (!product) {
          throw new Error('Product not found');
        }
        result = await productService.updateProduct(product.id, productData);
        setMessage({
          type: 'success',
          text: PRODUCT.SUCCESS_UPDATED(formData.uid),
        });
      } else {
        // Create new product
        result = await productService.createProduct(productData);
        setMessage({
          type: 'success',
          text: PRODUCT.SUCCESS_CREATED(result.uid || formData.uid),
        });

        // Reset form for create mode and generate new UID
        generatedUIDRef.current = null;
        const newUid = await fetchNextProductUid();
        setFormData({
          ...INITIAL_FORM,
          uid: newUid,
        });
        uploadedImages.forEach((img) => revokePreviewIfBlob(img.preview));
        setUploadedImages([]);
        setMainImageIndex(0);
      }

      // Scroll to top to see success message
      productRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

      setSaving(false);
    } catch (err) {
      console.error('Error saving product:', err);
      setMessage({ type: 'danger', text: err.message || 'Failed to save product. Please try again.' });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{PRODUCT.LOADING}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="product-page-bg" ref={productRef}>
      <div className="container-lg mt-2">
        {/* Header Section */}
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="product-header-icon">
              <FaBox size={28} />
            </div>
            <div>
              <h1 className="mb-0 product-title">
                {isEditMode ? PRODUCT.PAGE_TITLE_EDIT : PRODUCT.PAGE_TITLE_CREATE}
              </h1>
              <p className="mb-0 text-muted">
                {isEditMode ? PRODUCT.SUBTITLE_EDIT(formData.uid) : PRODUCT.SUBTITLE_CREATE}
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div className={`alert alert-${message.type} alert-dismissible fade show alert-rounded`} role="alert">
            <strong>{message.type === 'success' ? '✓ Success!' : message.type === 'warning' ? '⚠ Warning' : '✗ Error'}</strong> {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Product Images & Identification - Side by Side */}
          <div className="card border-0 shadow-sm mb-4 card-top-blue">
            <div className="card-body">
              <div className="row">
                {/* Images Section - Left Side */}
                <div className="col-lg-3 mb-3 mb-lg-0">
                  <h6 className="product-images-heading">Product Images <span className="text-danger">*</span></h6>
                  
                  {/* Main Image */}
                  {uploadedImages.length > 0 && (
                    <div className="product-main-image-wrapper">
                      <img
                        src={getImageDisplaySrc(uploadedImages[mainImageIndex])}
                        alt="Main Product"
                        className="product-main-image"
                      />
                    </div>
                  )}

                  {/* Upload Input */}
                  <input
                    type="file"
                    className={`form-control form-control-sm ${errors.images ? 'is-invalid' : ''}`}
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadedImages.length >= 5 || processingImages}
                  />
                  {errors.images && <div className="invalid-feedback d-block">{errors.images}</div>}
                  <small className="text-muted d-block mb-8">
                    {uploadedImages.length}/5 images
                    {processingImages && (
                      <span className="ms-2 text-primary fw-semibold">Removing backgrounds…</span>
                    )}
                  </small>
                  {!processingImages && (
                    <small className="text-muted d-block mb-8 product-hint-tiny">
                      Images are automatically optimized: background removed (PNG) and compressed to JPEG for faster uploads.
                    </small>
                  )}

                  {/* Thumbnails */}
                  {uploadedImages.length > 0 && (
                    <div className="product-thumbs">
                      {uploadedImages.map((img, idx) => (
                        <div
                          key={idx}
                          className="position-relative flex-shrink-0 cursor-pointer"
                          onClick={() => setMainImageIndex(idx)}
                        >
                          <img
                            src={getImageDisplaySrc(img)}
                            alt={`Thumb ${idx + 1}`}
                            className={`product-thumb-img ${mainImageIndex === idx ? 'active' : ''}`}
                          />
                          <button
                            type="button"
                            className="btn btn-sm thumb-remove-btn"
                            
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(idx);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Product Identification - Right Side */}
                <div className="col-lg-9">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600 product-field-label">UID/PID</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={formData.uid}
                        readOnly
                       
                      />
                      <small className="text-muted">Auto-generated</small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="itemNo" className="form-label fw-600 product-field-label">Item No. <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${errors.itemNo ? 'is-invalid' : ''}`}
                        id="itemNo"
                        name="itemNo"
                        value={formData.itemNo}
                        onChange={handleChange}
                        placeholder="Enter item number"
                       
                      />
                      {errors.itemNo && <div className="invalid-feedback d-block">{errors.itemNo}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600 product-field-label">Category <span className="text-danger">*</span></label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className={`form-select form-select-sm ${errors.category ? 'is-invalid' : ''}`}
                       
                      >
                        <option value="">Select Category</option>
                        {(submasters['category'] || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                      {errors.category && <div className="invalid-feedback d-block">{errors.category}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600 product-field-label">Product Name <span className="text-danger">*</span></label>
                      <select
                        name="productName"
                        value={formData.productName}
                        onChange={handleChange}
                        className={`form-select form-select-sm ${errors.productName ? 'is-invalid' : ''}`}
                       
                      >
                        <option value="">Select Product Name</option>
                        {(submasters['product-name'] || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                      {errors.productName && <div className="invalid-feedback d-block">{errors.productName}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-600 product-field-label">HS Code <span className="text-danger">*</span></label>
                      <select
                        name="hsCode"
                        value={formData.hsCode}
                        onChange={handleChange}
                        className={`form-select form-select-sm ${errors.hsCode ? 'is-invalid' : ''}`}
                       
                      >
                        <option value="">Select HS Code</option>
                        {(submasters['hs-code'] || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                      {errors.hsCode && <div className="invalid-feedback d-block">{errors.hsCode}</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Product Specifications */}
          <div className="card border-0 shadow-sm mb-4 card-top-red">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 h-red">
                <FaFileAlt className="me-2" />
                Product Specifications
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-6">
                  <TagInput
                    label="Materials"
                    selectedIds={formData.materials}
                    allItems={submasters['materials'] || []}
                    fieldName="materials"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.materials}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Suitable For"
                    selectedIds={formData.suitableFor}
                    allItems={submasters['suitable-for'] || []}
                    fieldName="suitableFor"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.suitableFor}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Collection"
                    selectedIds={formData.collection}
                    allItems={submasters['collection'] || []}
                    fieldName="collection"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.collection}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Color"
                    selectedIds={formData.color}
                    allItems={submasters['color'] || []}
                    fieldName="color"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.color}
                  />
                </div>
                <div className="col-lg-6">
                  <TagInput
                    label="Finish"
                    selectedIds={formData.finish}
                    allItems={submasters['finish'] || []}
                    fieldName="finish"
                    onAdd={handleMultiSelect}
                    onRemove={removeTag}
                    required
                    errors={errors.finish}
                  />
                </div>
                <div className="col-lg-6">
                  <label className="form-label fw-600 label-dark product-field-label">UOM - Count <span className="text-danger">*</span></label>
                  <select
                    name="uomCount"
                    value={formData.uomCount}
                    onChange={handleChange}
                    className={`form-select form-select-sm ${errors.uomCount ? 'is-invalid' : ''}`}
                   
                  >
                    <option value="">Select UOM Count</option>
                    {(submasters['uom-count'] || []).map((opt) => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))}
                  </select>
                  {errors.uomCount && <div className="invalid-feedback d-block">{errors.uomCount}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Dimensions & Weight */}
          <div className="card border-0 shadow-sm mb-4 card-top-amber">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 h-amber">
                <FaCube className="me-2" />
                Dimensions & Weight
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600 product-field-label">Length <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.lengthDimension ? 'is-invalid' : ''}`}
                    name="lengthDimension"
                    value={formData.lengthDimension}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                  {errors.lengthDimension && <div className="invalid-feedback d-block small-muted-075">{errors.lengthDimension}</div>}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600 product-field-label">Breadth <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.breadthDimension ? 'is-invalid' : ''}`}
                    name="breadthDimension"
                    value={formData.breadthDimension}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                  {errors.breadthDimension && <div className="invalid-feedback d-block small-muted-075">{errors.breadthDimension}</div>}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600 product-field-label">Height <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.heightDimension ? 'is-invalid' : ''}`}
                    name="heightDimension"
                    value={formData.heightDimension}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                  {errors.heightDimension && <div className="invalid-feedback d-block small-muted-075">{errors.heightDimension}</div>}
                </div>
                <div className="col-md-3 col-6 mb-3">
                  <label className="form-label fw-600 product-field-label">Unit</label>
                  <select
                    name="dimensionUnit"
                    value={formData.dimensionUnit}
                    onChange={handleChange}
                    className="form-select form-select-sm"
                   
                  >
                    <option value="cm">cm</option>
                    <option value="inch">inch</option>
                    <option value="mm">mm</option>
                    <option value="m">m</option>
                  </select>
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600 product-field-label">Net Weight <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.netWeight ? 'is-invalid' : ''}`}
                    name="netWeight"
                    value={formData.netWeight}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                  {errors.netWeight && <div className="invalid-feedback d-block small-muted-075">{errors.netWeight}</div>}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600 product-field-label">Weight Unit</label>
                  <select
                    name="weightUnit"
                    value={formData.weightUnit}
                    onChange={handleChange}
                    className="form-select form-select-sm"
                   
                  >
                    <option value="KG">KG</option>
                    <option value="lbs">lbs</option>
                    <option value="g">g</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & Logistics */}
          <div className="card border-0 shadow-sm mb-4 card-top-cyan">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 h-cyan">
                <FaWeight className="me-2" />
                Pricing & Logistics
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600 product-field-label">MOQ (Minimum Order Quantity) <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.moq ? 'is-invalid' : ''}`}
                    name="moq"
                    value={formData.moq}
                    onChange={handleChange}
                    placeholder="Enter MOQ"
                    min="1"
                   
                  />
                  {errors.moq && <div className="invalid-feedback d-block small-muted-075">{errors.moq}</div>}
                </div>
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label fw-600 product-field-label">Cost <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${errors.cost ? 'is-invalid' : ''}`}
                    name="cost"
                    value={formData.cost}
                    onChange={handleChange}
                    placeholder="Enter cost"
                    step="0.01"
                    min="0"
                  />
                  {errors.cost && <div className="invalid-feedback d-block small-muted-075">{errors.cost}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Inner Pack */}
          <div className="card border-0 shadow-sm mb-4 card-top-blue">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 h-blue">
                <FaCube className="me-2" />
                Inner Pack Details
              </h5>
            </div>
            <div className="card-body overflow-x-auto product-pack-section">
              <div className="table-responsive">
                <table className="table table-sm mb-0 table-compact">
                  <thead className="thead-muted">
                    <tr>
                      <th className="th-cell">Quantity Pack</th>
                      <th className="th-cell">Length</th>
                      <th className="th-cell">Breadth</th>
                      <th className="th-cell">Height</th>
                      <th className="th-cell">Gross Wt</th>
                      <th className="th-cell th-cell-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.innerPacks && formData.innerPacks.map((row, idx) => (
                      <tr key={idx}>
                        <td className="td-cell">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., 12 pcs per inner"
                            value={row.quantityPack}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'quantityPack', e.target.value)}
                           
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.length}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'length', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.breadth}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'breadth', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.height}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'height', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.netWt}
                            onChange={(e) => handlePackChange('innerPacks', idx, 'netWt', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell td-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-danger btn-xs"
                            onClick={() => removePackRow('innerPacks', idx)}
                            disabled={formData.innerPacks.length === 1}
                          >
                            <FaTrash size={10} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary product-pack-add-row"
                onClick={() => addPackRow('innerPacks')}
              >
                <FaPlus className="me-2" size={10} /> Add Row
              </button>
            </div>
          </div>

          {/* Master Pack */}
          <div className="card border-0 shadow-sm mb-4 card-top-green">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 h-green">
                <FaWeight className="me-2" />
                Master Pack Details
              </h5>
            </div>
            <div className="card-body overflow-x-auto product-pack-section">
              <div className="table-responsive">
                <table className="table table-sm mb-0 table-compact">
                  <thead className="thead-muted">
                    <tr>
                      <th className="th-cell">Quantity Pack</th>
                      <th className="th-cell">Length</th>
                      <th className="th-cell">Breadth</th>
                      <th className="th-cell">Height</th>
                      <th className="th-cell">Gross Wt</th>
                      <th className="th-cell th-cell-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.masterPacks && formData.masterPacks.map((row, idx) => (
                      <tr key={idx}>
                        <td className="td-cell">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., 48 pcs per master"
                            value={row.quantityPack}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'quantityPack', e.target.value)}
                           
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.length}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'length', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.breadth}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'breadth', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.height}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'height', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="0"
                            value={row.netWt}
                            onChange={(e) => handlePackChange('masterPacks', idx, 'netWt', e.target.value)}
                            step="0.01"
                          />
                        </td>
                        <td className="td-cell td-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-danger btn-xs"
                            onClick={() => removePackRow('masterPacks', idx)}
                            disabled={formData.masterPacks.length === 1}
                          >
                            <FaTrash size={10} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-success product-pack-add-row"
                onClick={() => addPackRow('masterPacks')}
              >
                <FaPlus className="me-2" size={10} /> Add Row
              </button>
            </div>
          </div>

          {/* Remarks */}
          <div className="card border-0 shadow-sm mb-4 card-top-purple">
            <div className="card-header bg-white border-0 pb-0">
              <h5 className="mb-0 h-purple">
                <FaFileAlt className="me-2" />
                Additional Information
              </h5>
            </div>
            <div className="card-body">
              <label htmlFor="remark" className="form-label fw-600 product-field-label">Remarks / Notes</label>
              <textarea
                className="form-control form-control-sm"
                id="remark"
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                rows="3"
                placeholder="Enter any additional remarks or notes"
               
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="mb-4 d-flex justify-content-between gap-2">
            <div className="d-flex gap-2">
              <button
                type="submit"
                disabled={saving || processingImages}
                className="btn submit-btn"
              >
                <FaCheckCircle className="me-2" />
                {saving ? (isEditMode ? 'Updating Product...' : 'Creating Product...') : (isEditMode ? 'Update Product' : 'Create Product')}
              </button>
              {isEditMode && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="btn btn-danger-solid"
                >
                  <FaTrash className="me-2" />
                  Delete Product
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductMaster;
